import { useQuery, useQueryErrorResetBoundary } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { AlertCircle, Passport } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import z from "zod";
import { Avatar } from "@/components/Atoms/Avatar/Avatar";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { Label } from "@/components/base/label";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { organizationQueryOptions } from "@/services/organization";

export const TabSchema = z.enum(["details", "permissions", "users"]);
type Tab = z.infer<typeof TabSchema>;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/roles/(roles)/$roleId",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/roles/(roles)/$roleId",
	});
	const [isOpen, setIsOpen] = useState(false);
	// Reset query errors when route changes to prevent remount loops
	const queryErrorResetBoundary = useQueryErrorResetBoundary();

	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";

	// Decode the role name from the URL parameter
	const roleNameFromUrl = decodeURIComponent(params.roleId);

	// Fetch all global roles
	const {
		data: globalRoles = [],
		isLoading,
		isError: hasError,
	} = useQuery(organizationQueryOptions.globalRoles.list());

	// Find the specific role by name
	const role = useMemo(
		() => globalRoles.find((r) => r.name === roleNameFromUrl),
		[globalRoles, roleNameFromUrl],
	);

	// Determine if this is a platform role based on scope
	const isPlatformRole = role?.scope === "Platform";

	// Open drawer when component mounts (route is accessed)
	useEffect(() => {
		setIsOpen(true);
	}, []);

	// Reset query error boundary on mount to prevent remount loops
	useEffect(() => {
		queryErrorResetBoundary.reset();
	}, [queryErrorResetBoundary]);

	// Handle drawer close - navigate back to parent route
	const handleClose = () => {
		setIsOpen(false);
		// Delay navigation to allow exit animation to complete (200ms)
		setTimeout(() => {
			navigate({
				to: "/admin/roles",
				replace: true,
			});
		}, 200);
	};

	// Handle drawer state change
	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			// If drawer is being closed, navigate back
			handleClose();
		}
	};

	// Determine drawer title and content
	const title = role?.name || t("admin.roles.drawer.title");
	const description = t("admin.roles.drawer.description");
	const tabs = isPlatformRole
		? []
		: [
				{ label: t("admin.roles.drawer.details"), id: "details" },
				{ label: t("admin.roles.drawer.permissions"), id: "permissions" },
			];

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			width="40%"
			backdropPreset="transparent"
		>
			{({ close }) => (
				<>
					<Drawer.Header
						variant="document"
						showDivider={false}
						icon={<Avatar placeholderIcon={Passport} size="md" alt={title} />}
						iconSize="md"
						iconTheme="light"
						title={
							<div className="flex items-center flex-row gap-4">
								<span>{title}</span>
								{role && (
									<Label size="sm" type="default" showIcon={false}>
										{isPlatformRole ? "Platform" : "Organization"}
									</Label>
								)}
							</div>
						}
						description={description}
						showCloseButton={true}
						closeButtonClassName="absolute top-2 right-2 z-10"
						onClose={() => {
							close();
							handleClose();
						}}
					/>
					<Drawer.Content>
						{isLoading ? (
							<div className="flex items-center justify-center h-full min-h-[400px]">
								<LoadingIndicator type="line-simple" size="xl" supportingText />
							</div>
						) : hasError ? (
							<div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
								<Illustration type="cloud" size="sm">
									<div className="bg-error-500 rounded-full p-3">
										<AlertCircle className="size-6" />
									</div>
								</Illustration>
								<div className="flex flex-col items-center justify-center pt-4">
									<p className="text-gray-900 font-semibold text-md">
										{t("admin.organizations.errors.errorLoadingPage")}
									</p>
									<div className="w-[60%]">
										<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
											{t("admin.organizations.errors.errorLoadingContent")}
										</p>
									</div>
								</div>
								{/* <div className="flex gap-3 justify-center">
									<Button
										color="secondary"
										size="md"
										onClick={() => {
											queryErrorResetBoundary.reset();
											router.invalidate();
										}}
									>
										{t("common.actions.retry")}
									</Button>
								</div> */}
							</div>
						) : (
							<div className="space-y-4">
								<NativeSelect
									aria-label={t("admin.roles.drawer.rollTabs")}
									value={currentTab}
									onChange={(event) =>
										navigate({
											to: `/admin/roles/$roleId/${event.target.value}`,
											params: { roleId: params.roleId },
										})
									}
									options={tabs.map((tab) => ({
										label: String(tab.label),
										value: tab.id,
									}))}
									className="w-80 md:hidden"
								/>
								<Tabs
									selectedKey={currentTab}
									onSelectionChange={(event) =>
										navigate({
											to: `/admin/roles/$roleId/${event}`,
											params: { roleId: params.roleId },
										})
									}
									className="w-full max-md:hidden"
								>
									<Tabs.List type="underline" items={tabs} fullWidth>
										{(tab) => <Tabs.Item {...tab} />}
									</Tabs.List>
								</Tabs>
								<TabContentWrapper tabKey={currentTab}>
									<Outlet />
								</TabContentWrapper>
							</div>
						)}
					</Drawer.Content>
					<Drawer.Footer>
						<div className="flex items-center justify-between w-full">
							{/* Left side - Error messages */}
							<div className="flex-1">
								{/* Child routes will handle their own errors */}
							</div>
							{/* Right side - Close button */}
							<div className="flex items-center gap-3">
								<Button color="secondary" size="md" onClick={handleClose}>
									{t("common.actions.close")}
								</Button>
							</div>
						</div>
					</Drawer.Footer>
				</>
			)}
		</Drawer>
	);
}
